import { Button, Container } from '../common';
import { Checkmark, Minus, Plus } from '../../icons';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { media } from '../../utils/styles';
import styled, { css } from 'styled-components';
import { PURCHASE_TYPES_ENUM } from '../../utils/enums';

import { DefaultVariant } from './Variants/DefaultVariant';
import { Freebie } from './Freebie';
import { Gallery } from './Gallery';
import { Helmet } from 'react-helmet';
import LazyLoad from 'react-lazyload';
import { Rating } from '../Rating';
import { ServiceUsps } from '../ServiceUsps';
import { StoreContext } from '../../store';
import { log } from '../../utils/logs';

export const AffiliateTop = ({ product, pricingType }) => {
  const { addItemsToCart } = useContext(StoreContext);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const didMountRef = useRef(false);
  const activeType = PURCHASE_TYPES_ENUM[pricingType];

  const variants = useMemo(() => {
    switch (activeType) {
      case PURCHASE_TYPES_ENUM.standart:
        return product.shopify_product.variants
          .filter(variant => variant.type === 'one-time order')
          .filter((_, index) => index <= 2)
          .reduce((acc, cur) => {
            return {
              ...acc,
              [cur.id]: cur,
            };
          }, {});
      case PURCHASE_TYPES_ENUM.subscription:
        return product.shopify_product.variants
          .filter(variant => variant.type === 'subscription')
          .reduce((acc, cur) => {
            return {
              ...acc,
              [cur.id]: cur,
            };
          }, {});
      case PURCHASE_TYPES_ENUM.influencer:
        return product.shopify_product.variants
          .filter(variant => variant.type === 'aff one-time order')
          .reduce((acc, cur) => {
            return {
              ...acc,
              [cur.id]: cur,
            };
          }, {});
      default:
        return {};
    }
  }, [product.shopify_product.variants, activeType]);

  const [activeVariant, setActiveVariant] = useState(variants[Object.keys(variants)[0]]);

  useEffect(() => {
    window._learnq = window._learnq || [];
    const item = {
      Name: product.shopify_product.title,
      ProductID: product.shopify_product.id,
      ImageURL: activeVariant.image,
      URL: window.location.protocol + '//' + window.location.host + window.location.pathname,
      Price: activeVariant.price,
      CompareAtPrice: activeVariant.compareAtPrice,
    };
    window._learnq.push(['track', 'Viewed Product', item]);
    window._learnq.push([
      'trackViewedItem',
      {
        Title: product.shopify_product.title,
        ItemId: product.shopify_product.id,
        ImageUrl: activeVariant.image,
        Url: window.location.protocol + '//' + window.location.host + window.location.pathname,
        Metadata: {
          Price: activeVariant.price,
          CompareAtPrice: activeVariant.compareAtPrice,
        },
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstImageRef = useRef(activeVariant.image);

  useEffect(() => {
    setActiveVariant(variants[Object.keys(variants)[0]]);
  }, [variants]);

  useEffect(() => {
    if (didMountRef.current) {
      log('select_content', {
        id: product.shopify_product.id,
        name: product.shopify_product.title,
        variant: activeVariant.title,
        quantity,
        price: activeVariant.price,
      });
    } else {
      didMountRef.current = true;
    }
  }, [quantity, activeVariant, product.shopify_product.id, product.shopify_product.title]);

  const checkIfHasFreebie = activeType === PURCHASE_TYPES_ENUM.subscription;

  const addToCart = async () => {
    setIsLoading(true);
    log('add_to_cart', {
      id: product.shopify_product.id,
      name: product.shopify_product.title,
      variant: activeVariant.title,
      quantity,
      price: activeVariant.price,
    });
    await addItemsToCart([
      {
        variantId: activeVariant.storefrontId,
        quantity,
      },
    ]);
    setIsLoading(false);
    setQuantity(1);
  };

  const subscribe = () => {
    setIsLoading(true);
    log('begin_checkout', {
      sum: activeVariant.price,
      items: [
        {
          id: product.shopify_product.id,
          name: product.shopify_product.title,
          variant: activeVariant.title,
          quantity: 1,
          price: activeVariant.price,
        },
      ],
    });
    const myLink = `https://${process.env.GATSBY_SHOPIFY_CUSTOM_DOMAIN}/cart/`;
    let apiUrl = `${myLink}?id=${activeVariant.id}&quantity=1&selling_plan=${activeVariant.selling_plan_id}`;
    window.location.href = apiUrl;
  };

  const renderVariants = () => {
    const keys = Object.keys(variants);
    return keys.map((variantId, index) => {
      const pricePerUnit =
        activeType === PURCHASE_TYPES_ENUM.subscription
          ? (
              (variants[variantId].price * 0.9) /
              variants[variantId].order_interval_frequency
            ).toFixed(2)
          : (variants[variantId].price / variants[variantId].title.match(/\d+/)[0]).toFixed(2);
      const subLabel =
        activeType === PURCHASE_TYPES_ENUM.subscription
          ? `$${pricePerUnit} / month`
          : `$${pricePerUnit} / ${product.unit}`;
      const variantTitle =
        activeType === PURCHASE_TYPES_ENUM.subscription
          ? `${variants[variantId].order_interval_frequency} month${
              variants[variantId].order_interval_frequency > 1 ? 's' : ''
            } supply`
          : variants[variantId].title;
      return (
        <DefaultVariant
          subLabel={subLabel}
          isAppFreebieActive={checkIfHasFreebie}
          isLast={index + 1 === keys.length}
          key={variantId}
          active={variantId === activeVariant.id.toString()}
          onClick={() => setActiveVariant(variants[variantId])}
          title={variantTitle}
        />
      );
    });
  };

  return (
    <>
      <Helmet>
        <link rel="preload" as="image" href={firstImageRef.current} />
      </Helmet>
      <Wrap>
        <Container>
          <Row>
            <MobileHeader>
              <Heading>{product.title}</Heading>
              <StyledRating total={product.reviews_count} rate={product.reviews_stars} />
              <Description>{product.short_description}</Description>
              {(product.manufactured_in_usa || product.fda_registered_facility) && (
                <Checkmarks>
                  {product.manufactured_in_usa && (
                    <span>
                      <Checkmark />
                      Manufactured in USA
                    </span>
                  )}
                  {product.fda_registered_facility && (
                    <span>
                      <Checkmark />
                      FDA registered facility
                    </span>
                  )}
                </Checkmarks>
              )}
              <ListDescription>
                <div dangerouslySetInnerHTML={{ __html: product.list_description.html }} />
              </ListDescription>
            </MobileHeader>
            <Visuals>
              <Badges>
                {product.badges.map((badge, i) => (
                  <LazyLoad once height={56} key={i}>
                    <img src={badge.image.url} alt={badge.image.alt || ''} width="56" height="56" />
                  </LazyLoad>
                ))}
              </Badges>
              <Images>
                <Gallery
                  alt={product.title}
                  image={activeVariant.image || ''}
                  bg={product.product_card_background}
                />
              </Images>
            </Visuals>
            <Content>
              <DesktopHeader>
                <Heading>{product.title}</Heading>
                <StyledRating total={product.reviews_count} rate={product.reviews_stars} />
                <Description>{product.short_description}</Description>
                {(product.manufactured_in_usa || product.fda_registered_facility) && (
                  <Checkmarks>
                    {product.manufactured_in_usa && (
                      <span>
                        <Checkmark />
                        Manufactured in USA
                      </span>
                    )}
                    {product.fda_registered_facility && (
                      <span>
                        <Checkmark />
                        FDA registered facility
                      </span>
                    )}
                  </Checkmarks>
                )}
                <ListDescription>
                  <div dangerouslySetInnerHTML={{ __html: product.list_description.html }} />
                </ListDescription>
              </DesktopHeader>
              {!product.out_of_stock ? (
                <>
                  <SelectionTitle>Choose your bundle</SelectionTitle>
                  <Variants>{renderVariants()}</Variants>
                  {checkIfHasFreebie && (
                    <Freebie
                      alwaysShowAppFreebie={activeType === PURCHASE_TYPES_ENUM.subscription}
                      hideEbookFreebie={true}
                    />
                  )}
                </>
              ) : null}
              <Buttons>
                {[PURCHASE_TYPES_ENUM.standart, PURCHASE_TYPES_ENUM.influencer].includes(
                  activeType,
                ) && (
                  <Quantity>
                    <button
                      aria-label="decrement quantity by 1"
                      onClick={() => setQuantity(q => (q !== 1 ? q - 1 : q))}
                      disabled={quantity === 1}
                    >
                      <Minus />
                    </button>
                    <span>{quantity}</span>
                    <button
                      aria-label="increment quantity by 1"
                      onClick={() => setQuantity(q => q + 1)}
                    >
                      <Plus />
                    </button>
                  </Quantity>
                )}
                {product.out_of_stock ? (
                  <Button grayscale as="div">
                    SOLD OUT
                  </Button>
                ) : (
                  <>
                    {activeType === PURCHASE_TYPES_ENUM.subscription ? (
                      <Button onClick={subscribe} isLoading={isLoading} disabled={isLoading}>
                        Subscribe
                      </Button>
                    ) : (
                      <Button onClick={addToCart} isLoading={isLoading} disabled={isLoading}>
                        Add to Cart
                      </Button>
                    )}
                  </>
                )}
              </Buttons>
              <ServiceUsps />
            </Content>
          </Row>
        </Container>
      </Wrap>
    </>
  );
};

const Checkmarks = styled.div`
  margin: 0 0 20px;
  svg {
    margin-right: 8px;
  }
  > span {
    display: inline-flex;
    align-items: center;
    margin-right: 16px;
    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
  }
  ${media.phone} {
    display: inline-flex;
    flex-direction: column;
    > span {
      margin: 0;
      &:first-of-type {
        margin: 0 0 10px;
      }
    }
  }
`;

const MobileHeader = styled.div`
  text-align: center;
  display: none;
  ${media.phone} {
    width: 100%;
    display: block;
  }
`;

const DesktopHeader = styled.div`
  ${media.phone} {
    display: none;
  }
`;

const Buttons = styled.div`
  display: flex;
  margin-top: 25px;
  ${Button} {
    flex: 1;
  }
`;

const Quantity = styled.div`
  background: #f0f4f9;
  border-radius: 100px;
  width: 124px;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    margin: 0 10px;
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;
    width: 20px;
    text-align: center;
  }
  button {
    width: 20px;
    height: 20px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  ${media.phone} {
    margin-right: 15px;
  }
`;

const Variants = styled.div`
  display: flex;
  padding-left: 1px;
  margin-bottom: 50px;
  ${p =>
    p.$withSpaces &&
    css`
      padding-left: 0;
      margin-left: -2px;
      margin-right: -2px;
    `}
`;

const SelectionTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  margin: 0 0 5px;
  margin-top: 17px;
  text-align: left;
`;

const StyledRating = styled(Rating)`
  margin: 10px 0 20px;
  svg {
    width: 12px;
  }
  ${media.phone} {
    display: inline-flex;
  }
`;

const Description = styled.p`
  font-weight: 300;
  font-size: 15px;
  line-height: 132.19%;
  margin: 0 0 20px;
`;

const ListDescription = styled.div`
  font-weight: 300;
  font-size: 15px;
  line-height: 150%;
  margin: 0 0 20px;
`;

const Heading = styled.h1`
  font-weight: bold;
  font-size: 28px;
  line-height: 34px;
`;

const Badges = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 30px;
  width: 72px;
  img {
    width: 100%;
  }
  > div {
    margin: 0 0 15px;
    &:last-of-type {
      margin: 0;
    }
  }
  ${media.smallDesktop} {
    width: 56px;
    margin-right: 15px;
  }
  ${media.phone} {
    flex-direction: row;
    justify-content: center;
    width: 100%;
    margin: 10px 0 0;
    > div {
      margin: 0 10px 0 0;
      &:last-of-type {
        margin: 0;
      }
    }
    img {
      width: 56px;
    }
  }
`;

const Images = styled.div`
  flex: 1;
  width: calc(100% + 140px);
  margin: -90px -70px;
  ${media.phone} {
    width: calc(100% + 80px);
    margin: -50px -40px;
  }
`;

const Row = styled.div`
  display: flex;
  ${media.phone} {
    flex-wrap: wrap;
  }
`;

const Visuals = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  overflow: hidden;
  ${media.phone} {
    flex: 0 0 100%;
    width: 100%;
    justify-content: center;
    flex-direction: column;
  }
`;

const Content = styled.div`
  width: 420px;
  max-width: 100%;
  margin-left: 120px;
  ${media.smallDesktop} {
    margin-left: 40px;
  }
  ${media.phone} {
    margin: 0;
    width: 100%;
    text-align: center;
  }
`;

const Wrap = styled.section`
  padding-top: 185px;
  ${media.phone} {
    padding-top: 155px;
  }
`;
